import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Fingerprint, Globe, Clock, UserSearch, ShieldCheck,
  ChevronRight, Zap, Copy, AlertTriangle, Loader2,
} from "lucide-react";

const VERDICT_COLOR = {
  malicious: "#FF3B30", confirmed_malicious: "#FF3B30",
  suspicious: "#FF9F0A", likely_malicious: "#FF9F0A",
  clean: "#34C759", benign: "#34C759",
  inconclusive: "#FFCC00", unknown: "#8E9196",
};

const TABS = [
  { k: "overview", label: "Overview", icon: ShieldCheck },
  { k: "enrichment", label: "Enrichment", icon: Zap },
  { k: "origin", label: "Attack Origin", icon: Globe },
  { k: "timemachine", label: "IOC Time Machine", icon: Clock },
  { k: "dossier", label: "Adversary Dossier", icon: UserSearch },
];

// project lat/lon → 800x400 map (equirectangular)
const project = (lat, lon) => {
  const x = ((lon + 180) / 360) * 800;
  const y = ((90 - lat) / 180) * 400;
  return [x, y];
};

export default function InvestigationDetail() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [tab, setTab] = useState("overview");
  const [selectedIOC, setSelectedIOC] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [streamProgress, setStreamProgress] = useState("");
  const streamRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/investigations/${id}`);
      setInv(data);
      return data;
    } catch (_e) {
      toast.error("Failed to load investigation");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Subscribe to SSE stream while the investigation is running
  useEffect(() => {
    if (!inv) return;
    if (inv.status !== "pending" && inv.status !== "running") return;
    if (streamRef.current) return; // already streaming

    let cancelled = false;
    const controller = new AbortController();
    streamRef.current = controller;

    (async () => {
      try {
        const token = localStorage.getItem("forensiq_token");
        const res = await fetch(`${API_BASE}/investigations/${id}/stream`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const frames = buf.split("\n\n");
          buf = frames.pop() || "";
          for (const f of frames) {
            const isDone = f.startsWith("event: done");
            const dataLine = f.split("\n").find((l) => l.startsWith("data:"));
            if (dataLine) {
              try {
                const p = JSON.parse(dataLine.slice(5).trim());
                if (p.progress) setStreamProgress(p.progress);
                if (p.status === "completed" || p.status === "failed") {
                  await load();
                }
              } catch (_e) { /* skip malformed frame */ }
            }
            if (isDone) {
              await load();
              return;
            }
          }
        }
      } catch (_e) {
        // silent
      } finally {
        streamRef.current = null;
      }
    })();

    return () => { cancelled = true; controller.abort(); streamRef.current = null; };
  // eslint-disable-next-line
  }, [inv?.status, id]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("forensiq_token");
      const res = await fetch(`${API_BASE}/investigations/${id}/report.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ForensiQ-${inv.case_number}-${inv.customer.customer_name.replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const copyHash = () => {
    if (!inv?.report_hash) return;
    navigator.clipboard.writeText(inv.report_hash);
    toast.success("Hash copied");
  };

  if (!inv) return <div className="p-8 text-[#8E9196] font-mono text-sm">Loading investigation…</div>;

  const narrative = inv.narrative || {};
  const dossier = inv.dossier || {};
  const enrichments = inv.enrichments || [];
  const currentIOC = enrichments[selectedIOC];
  const verdictKey = (narrative.verdict || "inconclusive").toLowerCase();
  const verdictColor = VERDICT_COLOR[verdictKey] || "#8E9196";
  const isRunning = inv.status === "pending" || inv.status === "running";

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <Link to="/investigations" data-testid="back-to-investigations" className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> All investigations
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6 flex-wrap">
        <div className="flex-1 min-w-[400px]">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="font-mono text-sm text-[#00F0FF]">{inv.case_number}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest border rounded-sm px-2 py-0.5"
              style={{ color: verdictColor, borderColor: verdictColor + "55" }}>
              {verdictKey.replace("_", " ")}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#8E9196]">
              confidence {narrative.confidence ?? "-"}%
            </span>
            {inv.status !== "completed" && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF9F0A] flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {inv.status}
              </span>
            )}
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl leading-tight">
            Investigation for {inv.customer.customer_name}
          </h1>
          <div className="mt-2 text-sm text-[#8E9196]">
            {inv.customer.organization && <>{inv.customer.organization} · </>}
            {inv.customer.industry} · {inv.customer.incident_type}
            <span className="text-[#FF9F0A] uppercase font-mono ml-2">{inv.customer.severity}</span>
          </div>
          {inv.tech_tags && inv.tech_tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="tech-tags">
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mr-1 self-center">Stack matches:</span>
              {inv.tech_tags.map((t) => (
                <span key={t}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm bg-[#B57BFF]/15 text-[#B57BFF] border border-[#B57BFF]/50">
                  🎯 {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={downloadPdf}
            disabled={downloading || inv.status !== "completed"}
            data-testid="download-report-btn"
            className="tracing-beam inline-flex items-center gap-2 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00F0FF]/80 px-4 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> {downloading ? "Preparing…" : "Download PDF Report"}
          </button>
        </div>
      </div>

      {/* Live progress banner while running */}
      {isRunning && (
        <div className="mb-6 tracing-beam border border-[#00F0FF]/50 bg-[#00F0FF]/[0.04] rounded-sm p-5" data-testid="running-banner">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" strokeWidth={1.75} />
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF]">Investigation in progress</div>
              <div className="text-white mt-0.5 font-medium">{streamProgress || inv.progress || "Preparing pipeline…"}</div>
            </div>
          </div>
          <div className="mt-4 h-1 rounded-sm bg-white/5 overflow-hidden">
            <div className="h-full bg-[#00F0FF] w-1/3 animate-pulse" />
          </div>
          <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            Live stream · Enriching from 6 sources (narrative + dossier in parallel)
          </div>
        </div>
      )}

      {/* Verdict banner (only after completed) */}
      {!isRunning && (
      <div className="mb-6 border rounded-sm p-5 bg-[#0a0a0a]" style={{ borderColor: verdictColor + "40" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: verdictColor }}>Executive verdict</div>
        <div className="font-display text-2xl mt-1 mb-2" style={{ color: verdictColor }}>
          {verdictKey.replace("_", " ").toUpperCase()}
        </div>
        <p className="text-[#E5E5E5]">{narrative.executive_summary || "Investigation narrative not generated yet."}</p>
      </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-white/10">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors ${
              tab === t.k ? "text-[#00F0FF] border-b-2 border-[#00F0FF]" : "text-[#8E9196] hover:text-white border-b-2 border-transparent"
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tab-content-overview">
          <div className="lg:col-span-2 space-y-4">
            <div className="border border-white/10 bg-[#121212] rounded-sm p-5">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Key findings</div>
              <div className="font-display text-lg mt-1 mb-3">What we discovered</div>
              <ul className="space-y-2">
                {(narrative.key_findings || []).map((k, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <ChevronRight className="w-4 h-4 text-[#00F0FF] shrink-0 mt-0.5" />
                    <span>{k}</span>
                  </li>
                ))}
                {!narrative.key_findings?.length && <li className="text-sm text-[#52525B]">No findings.</li>}
              </ul>
            </div>

            {(narrative.attack_chain || []).length > 0 && (
              <div className="border border-white/10 bg-[#121212] rounded-sm p-5">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">MITRE ATT&amp;CK</div>
                <div className="font-display text-lg mt-1 mb-3">Attack chain</div>
                <ol className="space-y-3">
                  {narrative.attack_chain.map((c, i) => (
                    <li key={i} className="border-l-2 border-[#00F0FF]/40 pl-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF]">
                        [{String(i + 1).padStart(2, "0")}] {c.stage}
                      </div>
                      <div className="text-sm mt-0.5">{c.description}</div>
                      <div className="font-mono text-xs text-[#8E9196] mt-0.5">Evidence: {c.evidence}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="border border-white/10 bg-[#121212] rounded-sm p-5">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Remediation plan</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#FF3B30] mb-2">Immediate (0–24h)</div>
                  <ul className="space-y-1.5 text-sm">
                    {(narrative.immediate_actions || []).map((a, i) => <li key={i}>› {a}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-[#34C759] mb-2">Long-term</div>
                  <ul className="space-y-1.5 text-sm">
                    {(narrative.long_term_recommendations || []).map((a, i) => <li key={i}>› {a}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border border-white/10 bg-[#121212] rounded-sm p-5">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Customer</div>
              <div className="mt-2 space-y-1 font-mono text-xs">
                <div><span className="text-[#8E9196]">Name:</span> <span className="text-white">{inv.customer.customer_name}</span></div>
                <div><span className="text-[#8E9196]">Org:</span> <span className="text-white">{inv.customer.organization || "—"}</span></div>
                <div><span className="text-[#8E9196]">Industry:</span> <span className="text-white">{inv.customer.industry || "—"}</span></div>
                <div><span className="text-[#8E9196]">Contact:</span> <span className="text-[#00F0FF]">{inv.customer.contact_email || "—"}</span></div>
                <div><span className="text-[#8E9196]">Assets:</span> <span className="text-white">{(inv.customer.affected_assets || []).join(", ") || "—"}</span></div>
              </div>
            </div>

            <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="ioc-summary-panel">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">IOC summary</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="border border-[#FF3B30]/40 rounded-sm p-2">
                  <div className="font-display font-black text-2xl text-[#FF3B30]">{narrative.iocs_summary?.malicious ?? "—"}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">malicious</div>
                </div>
                <div className="border border-[#FF9F0A]/40 rounded-sm p-2">
                  <div className="font-display font-black text-2xl text-[#FF9F0A]">{narrative.iocs_summary?.suspicious ?? "—"}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">suspicious</div>
                </div>
                <div className="border border-[#34C759]/40 rounded-sm p-2">
                  <div className="font-display font-black text-2xl text-[#34C759]">{narrative.iocs_summary?.clean ?? "—"}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#8E9196]">clean</div>
                </div>
              </div>
            </div>

            <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="custody-panel">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
                <Fingerprint className="w-3.5 h-3.5" /> Chain of custody
              </div>
              <div className="mt-3 space-y-2 text-xs font-mono">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8E9196] mb-0.5">Report SHA-256</div>
                  <div className="flex items-center gap-2">
                    <code className="text-[#00F0FF] break-all">{inv.report_hash || "pending"}</code>
                    {inv.report_hash && (
                      <button onClick={copyHash} data-testid="copy-hash-btn" className="text-[#8E9196] hover:text-white shrink-0">
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div><span className="text-[#8E9196]">Investigator:</span> <span className="text-white">{inv.investigator}</span></div>
                <div><span className="text-[#8E9196]">Opened:</span> <span>{new Date(inv.created_at).toLocaleString()}</span></div>
                <div><span className="text-[#8E9196]">Completed:</span> <span>{inv.completed_at ? new Date(inv.completed_at).toLocaleString() : "—"}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ENRICHMENT */}
      {tab === "enrichment" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="tab-content-enrichment">
          <div className="lg:col-span-1 border border-white/10 bg-[#121212] rounded-sm">
            <div className="px-4 py-3 border-b border-white/10 text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
              Indicators ({enrichments.length})
            </div>
            <div className="divide-y divide-white/5 max-h-[560px] overflow-y-auto">
              {enrichments.map((e, i) => (
                <button key={i} onClick={() => setSelectedIOC(i)}
                  data-testid={`ioc-item-${i}`}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    selectedIOC === i ? "bg-white/5 border-l-2 border-[#00F0FF]" : "hover:bg-white/[0.02] border-l-2 border-transparent"
                  }`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8E9196]">{e.indicator_type}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest ml-auto" style={{ color: VERDICT_COLOR[e.aggregate_verdict] }}>
                      {e.aggregate_verdict}
                    </span>
                  </div>
                  <div className="font-mono text-xs text-white mt-1 break-all">{e.indicator}</div>
                </button>
              ))}
              {enrichments.length === 0 && <div className="p-4 text-sm text-[#52525B]">No IOCs.</div>}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4">
            {currentIOC ? (
              <>
                <div className="border border-white/10 bg-[#121212] rounded-sm p-5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
                    {currentIOC.indicator_type.toUpperCase()} · aggregate verdict
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3 mt-1">
                    <div className="font-mono text-lg text-white break-all">{currentIOC.indicator}</div>
                    <span className="font-display font-black text-2xl uppercase" style={{ color: VERDICT_COLOR[currentIOC.aggregate_verdict] }}>
                      {currentIOC.aggregate_verdict}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(currentIOC.sources || []).map((s, i) => (
                    <div key={i} className="border border-white/10 bg-[#121212] rounded-sm p-4" data-testid={`source-card-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-display text-base">{s.source}</div>
                        <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm"
                          style={{ color: VERDICT_COLOR[s.verdict], borderColor: VERDICT_COLOR[s.verdict] + "55" }}>
                          {s.verdict}
                        </span>
                      </div>
                      <div className="space-y-1 text-xs font-mono text-[#8E9196]">
                        {Object.entries(s).filter(([k]) => !["source", "verdict", "raw", "note"].includes(k)).map(([k, v]) => (
                          <div key={k}>
                            <span className="text-[#52525B]">{k}:</span>{" "}
                            <span className="text-white">
                              {Array.isArray(v) ? (typeof v[0] === "object" ? JSON.stringify(v) : v.join(", ")) : String(v)}
                            </span>
                          </div>
                        ))}
                      </div>
                      {s.note && <div className="mt-2 text-[10px] text-[#52525B]">{s.note}</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm text-[#52525B] p-4">No IOC selected.</div>
            )}
          </div>
        </div>
      )}

      {/* ATTACK ORIGIN */}
      {tab === "origin" && (
        <div data-testid="tab-content-origin">
          <div className="border border-white/10 bg-[#0a0a0a] rounded-sm p-5 relative overflow-hidden">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-3 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5" /> Attack origin map · equirectangular projection
            </div>
            <svg viewBox="0 0 800 400" className="w-full h-auto" style={{ background: "#060606" }}>
              {/* subtle graticule */}
              {[...Array(9)].map((_, i) => (
                <line key={`h${i}`} x1="0" y1={(i * 400) / 8} x2="800" y2={(i * 400) / 8} stroke="rgba(255,255,255,0.04)" />
              ))}
              {[...Array(13)].map((_, i) => (
                <line key={`v${i}`} x1={(i * 800) / 12} y1="0" x2={(i * 800) / 12} y2="400" stroke="rgba(255,255,255,0.04)" />
              ))}
              {/* target center */}
              <circle cx="400" cy="200" r="8" fill="none" stroke="#00F0FF" strokeWidth="1" />
              <circle cx="400" cy="200" r="3" fill="#00F0FF" />
              <text x="410" y="196" fill="#00F0FF" fontSize="10" fontFamily="JetBrains Mono">
                TARGET · {inv.customer.customer_name}
              </text>
              {/* attackers */}
              {enrichments.filter((e) => e.attack_origin?.latitude != null).map((e, i) => {
                const [x, y] = project(e.attack_origin.latitude, e.attack_origin.longitude);
                return (
                  <g key={i}>
                    <path d={`M ${x} ${y} Q ${(x + 400) / 2} ${Math.min(y, 200) - 60} 400 200`}
                      stroke="#FF3B30" strokeWidth="1" fill="none" strokeDasharray="4 4">
                      <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="0.9s" repeatCount="indefinite" />
                    </path>
                    <circle cx={x} cy={y} r="5" fill="#FF3B30" />
                    <circle cx={x} cy={y} r="10" fill="none" stroke="#FF3B30" strokeOpacity="0.4">
                      <animate attributeName="r" values="6;14;6" dur="1.6s" repeatCount="indefinite" />
                      <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="1.6s" repeatCount="indefinite" />
                    </circle>
                    <text x={x + 8} y={y + 4} fill="#FF3B30" fontSize="9" fontFamily="JetBrains Mono">
                      {e.attack_origin.country} · {e.indicator}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            {enrichments.filter((e) => e.attack_origin).map((e, i) => (
              <div key={i} className="border border-white/10 bg-[#121212] rounded-sm p-4" data-testid={`origin-card-${i}`}>
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Origin of</div>
                <div className="font-mono text-sm text-[#00F0FF] break-all">{e.indicator}</div>
                <div className="mt-3 space-y-1 font-mono text-xs">
                  <div><span className="text-[#8E9196]">Country:</span> <span className="text-white">{e.attack_origin.country} — {e.attack_origin.country_name || ""}</span></div>
                  <div><span className="text-[#8E9196]">ASN / ISP:</span> <span className="text-white">{e.attack_origin.asn || "—"}</span></div>
                  <div><span className="text-[#8E9196]">Coordinates:</span> <span className="text-white">{e.attack_origin.latitude?.toFixed(2)}, {e.attack_origin.longitude?.toFixed(2)}</span></div>
                </div>
              </div>
            ))}
            {enrichments.filter((e) => e.attack_origin).length === 0 && (
              <div className="col-span-2 text-sm text-[#52525B] p-4">No geo-locatable indicators.</div>
            )}
          </div>
        </div>
      )}

      {/* TIME MACHINE */}
      {tab === "timemachine" && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="tab-content-timemachine">
          <div className="lg:col-span-1 border border-white/10 bg-[#121212] rounded-sm">
            <div className="px-4 py-3 border-b border-white/10 text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
              Choose an indicator
            </div>
            <div className="divide-y divide-white/5">
              {enrichments.map((e, i) => (
                <button key={i} onClick={() => setSelectedIOC(i)}
                  className={`w-full text-left px-4 py-2.5 font-mono text-xs ${
                    selectedIOC === i ? "bg-white/5 text-white border-l-2 border-[#00F0FF]" : "text-[#8E9196] hover:text-white border-l-2 border-transparent"
                  }`}>
                  <div className="uppercase text-[10px] tracking-widest text-[#52525B]">{e.indicator_type}</div>
                  <div className="truncate">{e.indicator}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 border border-white/10 bg-[#121212] rounded-sm p-5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">
              History of <span className="text-white">{currentIOC?.indicator}</span>
            </div>
            <div className="font-display text-2xl mt-1 mb-4">IOC Time Machine</div>
            <div className="relative pl-6 border-l border-white/10 space-y-5">
              {(currentIOC?.time_machine || []).map((t, i) => (
                <div key={i} className="relative">
                  <span className="absolute -left-[26px] top-1 w-3 h-3 rounded-full bg-[#00F0FF] border-2 border-[#060606]" />
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF]">{t.date}</div>
                  <div className="text-sm font-medium mt-0.5">{t.title}</div>
                  <div className="text-xs text-[#8E9196] mt-0.5">{t.detail}</div>
                </div>
              ))}
              {!currentIOC?.time_machine?.length && <div className="text-sm text-[#52525B]">No history.</div>}
            </div>
          </div>
        </div>
      )}

      {/* DOSSIER */}
      {tab === "dossier" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tab-content-dossier">
          <div className="lg:col-span-2 border border-white/10 bg-[#121212] rounded-sm p-6">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
              <UserSearch className="w-3.5 h-3.5" /> AI-inferred adversary profile
            </div>
            <div className="mt-1 flex items-baseline gap-4 flex-wrap">
              <div className="font-display font-black text-4xl">{dossier.likely_actor || "Unknown actor"}</div>
              <span className="font-mono text-xs text-[#8E9196] uppercase tracking-widest">
                confidence {dossier.confidence ?? "-"}%
              </span>
            </div>
            {dossier.actor_aliases?.length ? (
              <div className="mt-2 text-xs font-mono text-[#8E9196]">
                aka {dossier.actor_aliases.join(", ")}
              </div>
            ) : null}
            <p className="mt-4 text-[#E5E5E5]">{dossier.summary || "—"}</p>

            {dossier.typical_ttps?.length ? (
              <div className="mt-6">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-2">Typical TTPs</div>
                <div className="flex flex-wrap gap-1.5">
                  {dossier.typical_ttps.map((t) => (
                    <span key={t} className="font-mono text-[11px] border border-[#FF9F0A]/40 text-[#FF9F0A] rounded-sm px-2 py-0.5">{t}</span>
                  ))}
                </div>
              </div>
            ) : null}

            {dossier.recommended_hunt_queries?.length ? (
              <div className="mt-6">
                <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] mb-2">Recommended hunt queries</div>
                <div className="space-y-1.5">
                  {dossier.recommended_hunt_queries.map((q, i) => (
                    <code key={i} className="block font-mono text-xs text-[#00F0FF] border border-white/10 rounded-sm p-2">{q}</code>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="border border-white/10 bg-[#121212] rounded-sm p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Motivation</div>
              <div className="font-display text-xl mt-1">{(dossier.motivation || "unknown").toUpperCase()}</div>
            </div>
            <div className="border border-white/10 bg-[#121212] rounded-sm p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Sophistication</div>
              <div className="font-display text-xl mt-1">{(dossier.sophistication || "unknown").toUpperCase()}</div>
            </div>
            {dossier.targeted_industries?.length ? (
              <div className="border border-white/10 bg-[#121212] rounded-sm p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Targeted industries</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {dossier.targeted_industries.map((i) => <span key={i} className="font-mono text-xs border border-white/15 rounded-sm px-2 py-0.5">{i}</span>)}
                </div>
              </div>
            ) : null}
            {dossier.similar_campaigns?.length ? (
              <div className="border border-white/10 bg-[#121212] rounded-sm p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Similar campaigns</div>
                <ul className="mt-2 space-y-1 text-xs font-mono text-[#E5E5E5]">
                  {dossier.similar_campaigns.map((c, i) => <li key={i}>• {c}</li>)}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
